import { Box, CardActions, CardContent } from "@mui/material";
import * as React from "react";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "../../../framework/appUtils";
import { ITypedOption } from "../../../models/common/typedOption";
import { IRecordPageEdit } from "../../../models/record/recordPageEdit";
import * as recordService from "../../../models/services/recordService";
import { IAppSessionState } from "../../../models/store/storeTypes";
import { IUnitNodePermissions } from "../../../models/unit/unitNodePermissions";
import { HeaderMenuItem, NavigationMenu } from "../../app/NavigationMenu";
import { UserMenu } from "../../app/UserMenu";
import { ConfirmationButton } from "../../framework/Button/ConfirmationButton";
import { MainListContainer } from "source/components/framework/List/MainListContainer";
import { PageContainer } from "../../framework/Page/PageContainer";
import { PageContent } from "../../framework/Page/PageContent";
import { PageTabs } from "../../framework/Page/PageTabs";
import { PageTitle } from "../../framework/Page/PageTitle";
import { PageTopBar } from "../../framework/Page/PageTopBar";
import { RecordEdit as RecordEditView, RecordEditViewMode } from "../RecordEdit/RecordEdit";
import { AppButton } from "ui/Scripts/source/components/framework/Button/AppButton";

// RecordPageEdit
export interface IRecordPageEditProps {
    appState: IAppSessionState;
    dataId: number;
    pageEdit: IRecordPageEdit;
    onGetRecordsPage: () => void;
    onLogout: () => void;
    onSetCulture: (culture: string) => void;
    onSaveUserParameters: (parameters: ITypedOption[]) => void;
    onRefreshSessionState: () => void;
}

interface IRecordPageEditState {
    showNavPanel: boolean;
    activeMenuItem: RecordEditViewMode;
    settingsActive: boolean;
    // Properties
    showUnitNodePermissionsDialog: boolean;
    unitNodePermissions: IUnitNodePermissions;
}

export class RecordPageEdit extends React.Component<IRecordPageEditProps, IRecordPageEditState> {
    private recordEditViewRef = React.createRef<RecordEditView>();

    constructor(props: IRecordPageEditProps) {
        super(props);
        this.state = {
            showNavPanel: true, //props.sessionState.getShowPageNavigation(),
            activeMenuItem: RecordEditViewMode.General,
            settingsActive: false,
            showUnitNodePermissionsDialog: false,
            unitNodePermissions: null
        };
    }

    componentDidMount(): void {
        this.props.onGetRecordsPage();
    }

    handleToggleShowNavPanel = () => {
        this.setState({ showNavPanel: !this.state.showNavPanel });
    };

    handleToggleActiveMenuItem = (userPageMenuItem: RecordEditViewMode) => {
        this.setState({
            activeMenuItem: this.state.activeMenuItem !== userPageMenuItem ? userPageMenuItem : RecordEditViewMode.None,
            settingsActive: false
        });
    };

    handleSave = async () => {
        if (this.recordEditViewRef && this.recordEditViewRef.current) {
            this.recordEditViewRef.current.save();
        }
    };

    handlePrint = async () => {
        const fileName = await AppUtils.callService(() => recordService.getRecordPrint("", this.props.appState.state.companyId));
        if (!fileName) return;
        AppUtils.showSuccessMessage(String.format(Translations.AttachmentParameterDownloaded, fileName));
    };

    handleNavigateOutCheck = async (): Promise<boolean> => {
        if (this.recordEditViewRef && this.recordEditViewRef.current) {
            return await this.recordEditViewRef.current.canNavigateOut();
        } else {
            return true;
        }
    };

    getAttahmentsCount = (): number => {
        if (this.recordEditViewRef && this.recordEditViewRef.current) {
            return this.recordEditViewRef.current.getAttahmentsCount();
        } else {
            return 0;
        }
    };

    getRecordName = (): string => {
        if (this.recordEditViewRef && this.recordEditViewRef.current) {
            return this.recordEditViewRef.current.getRecordName();
        } else {
            return "";
        }
    };

    render() {
        const props = this.props;
        const state = this.state;
        const sessionState = props.appState.state;
        const attachmentsCount = this.getAttahmentsCount();
        const recordName = this.getRecordName();
        return (
            <PageContainer>
                <NavigationMenu
                    appState={props.appState}
                    activeMenuItem={HeaderMenuItem.UserRecords}
                    activeCompanyId={sessionState.companyId}
                />
                <PageContent>
                    <PageTopBar>
                        <UserMenu
                            appState={props.appState}
                            onLogout={props.onLogout}
                            onSetCulture={props.onSetCulture}
                            onRefreshSessionState={props.onRefreshSessionState}
                        />
                    </PageTopBar>
                    <>
                        <PageTitle title={Translations.RecordUser + (recordName ? " - " + recordName : "")}>
                            <></>
                        </PageTitle>
                        <MainListContainer>
                            <CardContent>
                                <PageTabs
                                    activeTabId={state.activeMenuItem}
                                    tabs={[
                                        { id: RecordEditViewMode.General, title: Translations.BasicInformation },
                                        { id: RecordEditViewMode.Familiarization, title: Translations.Familiarization },
                                        { id: RecordEditViewMode.Qualifications, title: Translations.Qualifications },
                                        { id: RecordEditViewMode.Education, title: Translations.Education + " / " + Translations.Competency },
                                        { id: RecordEditViewMode.Employment, title: Translations.Employment },
                                        { id: RecordEditViewMode.NextOfKin, title: Translations.NextOfKin },
                                        { id: RecordEditViewMode.Salary, title: Translations.Salary, hidden: !(!!props.pageEdit && props.pageEdit.hasSheet) },
                                        { id: RecordEditViewMode.Attachments, title: Translations.Attachments, badgeContent: attachmentsCount },
                                        { id: RecordEditViewMode.TransferredProperty, title: Translations.TransferredProperties },
                                    ]}
                                    changeTab={this.handleToggleActiveMenuItem}
                                />
                                <Box sx={{ height: "calc(100vh - 298px)", paddingTop: ".5rem", overflowX: "hidden", overflowY: "auto", display: "flex", flexDirection: "column", flexGrow: 2 }}>
                                    <RecordEditView
                                        ref={this.recordEditViewRef}
                                        onDialog={false}
                                        sessionState={sessionState}
                                        companyId={sessionState.companyId}
                                        recordId={""}
                                        viewMode={state.activeMenuItem}
                                        onRefreshSessionState={props.onRefreshSessionState}
                                    />
                                </Box>
                            </CardContent>
                            <CardActions sx={{ justifyContent: "flex-end", paddingX: "1rem" }}>
                                <AppButton variant="outlined" onClick={this.handlePrint} sx={{ marginRight: "1rem" }}>
                                    {Translations.Print}
                                </AppButton>
                                <ConfirmationButton onClick={this.handleSave}>
                                    {Translations.Save}
                                </ConfirmationButton>
                            </CardActions>
                        </MainListContainer>
                    </>
                </PageContent>
            </PageContainer>
        );
    }
}